import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const sendVerificationCodeFn   = httpsCallable(functions, 'sendVerificationCode');
const verifyEmailCodeFn        = httpsCallable(functions, 'verifyEmailCode');
const resetPasswordWithCodeFn  = httpsCallable(functions, 'resetPasswordWithCode');

/**
 * Requests a fresh 6-digit code be emailed to `email`. Resolves once the
 * Cloud Function confirms the email was sent — the code itself is never
 * returned to the client (the server never sends it back, by design).
 *
 * `purpose`: 'register' (default) requires that NO account exists yet for the
 * address — the "already registered" check happens here rather than at the end
 * of signup, so the user finds out before filling in the rest of the form.
 * 'reset' is the mirror image: it requires an account to already exist.
 *
 * Throws a Firebase `FirebaseError` on failure — check `err.code`:
 *   'functions/invalid-argument'   → not a valid email
 *   'functions/already-exists'     → (purpose: 'register') email already has an account
 *   'functions/not-found'          → (purpose: 'reset') no account with that email
 *   'functions/resource-exhausted' → resend cooldown or hourly cap hit
 */
export async function sendVerificationCode(email, purpose = 'register') {
  const { data } = await sendVerificationCodeFn({ email, purpose });
  return data; // { success: true }
}

/**
 * Checks a 6-digit code against what was emailed to `email`. Resolves
 * { success: true } once verified — the code is consumed (one-time use).
 * Used by registration only — see resetPasswordWithCode for why password
 * reset can't split "verify" from the sensitive action into two calls.
 *
 * Throws a Firebase `FirebaseError` on failure — check `err.code`:
 *   'functions/not-found'          → no code was ever requested for this email
 *   'functions/deadline-exceeded'  → code expired (10 min)
 *   'functions/resource-exhausted' → too many wrong guesses
 *   'functions/invalid-argument'   → wrong code (or malformed input)
 */
export async function verifyEmailCode(email, code) {
  const { data } = await verifyEmailCodeFn({ email, code });
  return data; // { success: true }
}

/**
 * Validates the 6-digit code AND sets `newPassword` on the account for
 * `email`, in one server-side call — the code is re-checked on this exact
 * request, never trusted from a prior "verified" flag. Resolves
 * { success: true } once the password has actually changed.
 *
 * Throws a Firebase `FirebaseError` on failure — check `err.code`:
 *   'functions/not-found'          → no code pending, or no account with that email
 *   'functions/deadline-exceeded'  → code expired (10 min)
 *   'functions/resource-exhausted' → too many wrong guesses
 *   'functions/invalid-argument'   → wrong code, or password too short
 */
export async function resetPasswordWithCode(email, code, newPassword) {
  const { data } = await resetPasswordWithCodeFn({ email, code, newPassword });
  return data; // { success: true }
}
